import { useId, useState } from 'react';

import { Button, Dialog, Field, Input } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { loiTheoTruong, maLoi } from '../../../lib/errors';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { DAI_TOI_DA, kiemEmail, noiNhanThu } from '../nghiep-vu/thao-tac';
import type { LoiEmail } from '../nghiep-vu/thao-tac';
import type { UserProfile } from '../types';

const KHOA_LOI: Record<LoiEmail, KhoaDich> = {
  trong: 'nguoiDung.fEmailNhanThuTrong',
  dinhDang: 'nguoiDung.fEmailSai',
  dai: 'nguoiDung.fQuaDai',
};

/** Lỗi nói về chính địa chỉ vừa nhập: đặt dưới ô, không gom vào dải chung. */
const LOI_CUA_O = new Set(['EMAIL_NOT_INVITABLE', 'INVALID_ARGUMENT']);

/**
 * Hộp đổi email nhận thư của một người (ADR-0101,
 * `PUT /api/v1/tenants/{id}/users/{user_id}/email-nhan-thu`).
 *
 * Email nhận thư nhận liên kết đặt lại mật khẩu, nên đây là hộp xác nhận: tiêu
 * đề đặt câu hỏi, thân nói hậu quả (thư đặt lại mật khẩu đi tới địa chỉ mới,
 * người đó bị đăng xuất và phải xác minh lại), nút nhắc lại đúng việc. Tên đăng
 * nhập không đổi. Lỗi hiện trong hộp, hộp không đóng. Dùng ở cả cổng tenant
 * (Cấu hình người dùng) và cổng nền tảng (tab Người dùng của chi tiết tenant).
 */
export function HopDoiEmailNhanThu({
  nguoi,
  open,
  dangChay,
  onClose,
  onDoi,
}: {
  nguoi: Pick<UserProfile, 'display_name' | 'email' | 'email_nhan_thu'>;
  open: boolean;
  dangChay: boolean;
  onClose: () => void;
  onDoi: (email: string) => Promise<unknown>;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const [email, setEmail] = useState('');
  const [daGui, setDaGui] = useState(false);
  const [loiServer, setLoiServer] = useState<unknown>(null);

  const hienTai = (noiNhanThu(nguoi) ?? '').toLowerCase();
  const ma = kiemEmail(email);
  const trung = !ma && email.trim().toLowerCase() === hienTai;
  const loiServerCuaO = loiServer && LOI_CUA_O.has(maLoi(loiServer) ?? '') ? loiServer : null;
  const loiO =
    (ma && (daGui || ma === 'dai') ? t(KHOA_LOI[ma], { so: DAI_TOI_DA.email }) : undefined) ??
    (trung && daGui ? t('nguoiDung.fEmailNhanThuTrung') : undefined) ??
    (loiServerCuaO
      ? (loiTheoTruong(loiServerCuaO)['/email_nhan_thu'] ??
        // Câu chung của server nói "mời"; ở đây là đổi địa chỉ. Vẫn không nói địa chỉ đã thuộc ai (ADR-0099).
        (maLoi(loiServerCuaO) === 'EMAIL_NOT_INVITABLE'
          ? t('nguoiDung.fEmailNhanThuKhongDung')
          : dichLoi(loiServerCuaO)))
      : undefined);
  const loiChung = loiServer && !loiServerCuaO ? dichLoi(loiServer) : null;

  const gui = async () => {
    setDaGui(true);
    if (ma || trung || dangChay) return;
    setLoiServer(null);
    try {
      await onDoi(email.trim());
    } catch (err) {
      setLoiServer(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('nguoiDung.xnDoiEmailTieuDe', { ten: nguoi.display_name })}
      width={480}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dangChay}>
              {t('nguoiDung.doiEmailNhanThu')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <p className="text-sm leading-relaxed text-content-secondary">{t('nguoiDung.xnDoiEmailThan')}</p>
        <Field label={t('nguoiDung.fEmailNhanThuMoi')} required error={loiO}>
          {(p) => (
            <Input
              {...p}
              autoFocus
              type="email"
              inputMode="email"
              autoComplete="off"
              spellCheck={false}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setLoiServer(null);
              }}
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}
